import React from "react";
import { useTranslation } from "react-i18next";
import { initials } from "../../data/alumniStories";

/**
 * A learner's post screenshot, cropped to a top-aligned window with a soft fade.
 * Clicking opens the full screenshot (see StoryLightbox).
 *  - variant "marquee": fixed-size card for the homepage carousel
 *  - variant "grid": natural-width card for the masonry on /alumni-stories
 */
export default function StoryCard({ story, onOpen, variant = "grid" }) {
  const { t } = useTranslation();
  const isMarquee = variant === "marquee";

  return (
    <button
      type="button"
      onClick={() => onOpen(story)}
      aria-label={`${t("stories.viewPost")}: ${story.name}`}
      className={`group text-left bg-white rounded-[20px] border border-gray-100 shadow-sm overflow-hidden flex flex-col
        hover:-translate-y-2 hover:shadow-xl hover:border-primary/20 transition-all duration-300
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
        ${isMarquee ? "w-[280px] lg:w-[320px] shrink-0" : "w-full break-inside-avoid mb-6"}`}
    >
      <div className={`relative overflow-hidden bg-gray-50 ${isMarquee ? "h-[340px] lg:h-[380px]" : "max-h-[460px]"}`}>
        <img
          src={story.image}
          alt={story.alt}
          width={900}
          height={story.height}
          loading="lazy"
          decoding="async"
          className={`w-full block object-top ${isMarquee ? "h-full object-cover" : "h-auto"}`}
        />
        <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-white via-white/70 to-transparent pointer-events-none" />
        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-[#0b0f19] text-white text-[10px] font-bold uppercase tracking-wider rounded-full px-3 py-1.5 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 group-focus-visible:opacity-100 group-focus-visible:translate-y-0 transition-all duration-300">
          {t("stories.viewPost")}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3 h-3">
            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </span>
      </div>

      <div className="flex items-center gap-3 px-5 py-4 border-t border-gray-100 mt-auto">
        <div className="w-10 h-10 shrink-0 rounded-full bg-primary/10 text-primary font-bold text-[13px] flex items-center justify-center">
          {initials(story.name)}
        </div>
        <div className="min-w-0">
          <h4 className="font-bold text-[#0b0f19] text-[14px] leading-none truncate">{story.name}</h4>
          <p className="text-gray-500 text-[11px] mt-1 truncate">{story.headline}</p>
          <p className="text-primary font-bold tracking-widest text-[10px] uppercase mt-1.5 truncate">{story.tag}</p>
        </div>
      </div>
    </button>
  );
}
